import { Component, OnInit, OnDestroy } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormControl,
  ValidationErrors,
  Validators,
  ValidatorFn,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, Observable, of } from 'rxjs';
import {
  takeUntil,
  take,
  debounceTime,
  distinctUntilChanged,
  map,
  startWith,
} from 'rxjs/operators';
import {
  PrintAttributeDefinition,
  PrintAttributeSavePayload,
  PrintAttributeType,
  PrintAttributeStatus,
  AssetData,
} from '../../../models/print-attribute.model';
import { PrintAttributeService } from '../../../services/print-attribute.service';
import { DEFAULT_SNACKBAR_CONFIG } from 'src/app/@shared/constants/site.constants';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from 'src/app/@shared';
import { DivisionService } from '../../../services/division.service';
import { EventTypeService } from '../../../services/event-type.service';
import { BannersService } from '../../../services/banners.service';
import { AssetsService } from '../../../services/assets.service';
import { AssetsSearchDialogComponent } from '../../assets-search-dialog/assets-search-dialog.component';
import { SearchAsset } from '../../../models/search-state.model';
import * as dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatChipInputEvent } from '@angular/material/chips';
import { AuthorizationService } from 'src/app/@shared/services/authorization.service';


interface DivisionOption {
  id: string;
  name: string;
}

interface DynamicField {
  id: string;
  label: string;
  type: 'text' | 'date' | 'number' | 'url' | 'textarea';
  value?: string;
  placeholder?: string;
  /** When true, input is read-only (populated from asset picker); control stays enabled for validators. */
  readOnly?: boolean;
}

@Component({
  selector: 'app-create-print-attribute',
  templateUrl: './create-print-attribute.component.html',
  styleUrls: ['./create-print-attribute.component.scss'],
})
export class CreatePrintAttributeComponent implements OnInit, OnDestroy {
  AttributeTypes = PrintAttributeType;
  AttributeStatuses = PrintAttributeStatus;
  private destroy$ = new Subject<void>();

  formGroup!: FormGroup;
  printAttributeId: string = '0';
  isEditMode: boolean = false;
  sectionTitle: string = 'New Attribute';
  dynamicFields: DynamicField[] = [];
  selectedAttributeType: string = '';
  imageRequiredTypes: string[] = [
    PrintAttributeType.url,
    PrintAttributeType.creative,
    PrintAttributeType.header,
    PrintAttributeType.market_place,
    PrintAttributeType.abev,
  ];
  showImageUpload: boolean = false;
  selectedAssetPreview: AssetData | null = null;
  previewImageLoadFailed = false;

  // Observables for dropdown data
  eventTypes$: Observable<DivisionOption[]> = of([]);
  banners$: Observable<DivisionOption[]> = of([]);
  divisions$: Observable<DivisionOption[]> = of([]);
  
  // Local arrays for binding
  eventTypes: DivisionOption[] = [];
  banners: DivisionOption[] = [];
  divisions: DivisionOption[] = [];
  private previousDivisionSelection: string[] = [];
  readOnlyMode = false;

  /** `Detail.Data` form keys using `mat-datepicker` (same format as event-details on save). */
  private dateDataFieldIds: string[] = [];

  // Dynamic field definitions by attribute type
  private dynamicFieldDefs: { [key: string]: DynamicField[] } = {
    'address': [
      { id: 'StoreAddress', label: 'Store Address', type: 'text' }
    ],
    'sale_dates': [
      { id: 'SaleStartDate', label: 'Sale Start Date', type: 'date' },
      { id: 'SaleEndDate', label: 'Sale End Date', type: 'date' },
    ],
    'url': [
      { id: 'Url', label: 'URL', type: 'url', placeholder: 'https://example.com' },
    ],
    'printer_code': [
        { id: 'PrinterCode', label: 'Printer Code', type: 'text' },
      { id: 'RoutingDestination', label: 'Routing Destination', type: 'text' },
    ],
    'header': [
      { id: 'HeaderText', label: 'Header Text', type: 'text' },
      { id: 'SubheaderText', label: 'Subheader Text', type: 'text' },
    ],
    'creative': [
      { id: 'CreativeAssetId', label: 'Asset ID', type: 'text' },
      { id: 'PlacementNotes', label: 'Placement Notes', type: 'text' },
    ],
    'market_place': [
      { id: 'MarketplaceName', label: 'Marketplace Name', type: 'text' },
      { id: 'PromoBlockContent', label: 'Promo Block Content', type: 'text' },
    ],
    'offer_event': [
      { id: 'EventLabel', label: 'Event Label', type: 'text' },
      { id: 'BrandOverlayText', label: 'Brand Overlay Text', type: 'text' },
    ],
    'abev': [
      { id: 'LicenseType', label: 'License Type', type: 'text' },
      { id: 'AbevLayoutNotes', label: 'ABEV Layout Notes', type: 'text' },
    ],
    'dry_version': [
      { id: 'ReplacementContent', label: 'Replacement Content', type: 'text' },
      { id: 'DryVersionLayoutNotes', label: 'Layout Notes', type: 'text' },
    ],
  };

  /** Sale start must be on or before end (same day allowed). */
  private readonly saleDatesRangeValidator: ValidatorFn = (
    group: AbstractControl,
  ): ValidationErrors | null => {
    const g = group as FormGroup;
    const rawStart = g.get('SaleStartDate')?.value;
    const rawEnd = g.get('SaleEndDate')?.value;
    if (rawStart == null || rawEnd == null || rawStart === '' || rawEnd === '') {
      return null;
    }
    const start = dayjs(rawStart instanceof Date ? rawStart : String(rawStart)).startOf('day');
    const end = dayjs(rawEnd instanceof Date ? rawEnd : String(rawEnd)).startOf('day');
    if (!start.isValid() || !end.isValid()) {
      return null;
    }
    return start.isAfter(end, 'day') ? { saleDateRange: true } : null;
  };

  attributeTypeOptions: { id: string; label: string }[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private printAttributeService: PrintAttributeService,
    private fb: FormBuilder,
    private matSnackbar: MatSnackBar,
    private dialog: MatDialog,
    private divisionService: DivisionService<any>,
    private eventTypeService: EventTypeService<any>,
    private bannersService: BannersService<any>,
    private assetsService: AssetsService<any>,
    private authorizationService: AuthorizationService
  ) {

    this.printAttributeService.printAttributeTypes$.pipe(takeUntil(this.destroy$)).subscribe((types) => {
      this.attributeTypeOptions = types;
    });
    /** Matches {@link PrintAttributeSavePayload} — patch from API / pass to save with small save-time tweaks only. */
    this.formGroup = this.fb.group({
      Detail: this.fb.group({
        Id: [''],
        ClientKey: ['', [Validators.required]],
        UserId: [''],
        PrintAttributeName: ['', [Validators.required]],
        PrintAttributeType: ['', [Validators.required]],
        Description: [''],
        Status: ['active', [Validators.required]],
        LogicalVersions: [''],
        LogicalPrintPage: [''],
        Data: this.fb.group({}),
      }),
      DivisionIds: ['', [Validators.required]],
      DivisionNames: [''],
      EventTypeName: [''],
      BannerName: [''],
      AssetNames: [''],
      AssetId: [''],
      EventTypeId: ['', [Validators.required]],
      BannerId: [''],
    });

    // Watch for changes to auto-fill Name field and render dynamic fields
    this.detailForm
      .get('PrintAttributeType')
      ?.valueChanges.pipe(
        takeUntil(this.destroy$),
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe((attributeType) => {
        this.selectedAttributeType = attributeType;
        this.showImageUpload = this.isAssetApplicableType(attributeType);
        if (!this.showImageUpload) {
          this.removeSelectedAsset();
        }
        this.renderDynamicFields(attributeType);
        this.updateAssetRequirementValidators();
      });

    
    this.formGroup.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      console.log(this.formGroup.controls);
    });
  }

  /** Root payload shape; `Detail` includes dynamic fields under nested `Data` `FormGroup` (serialized to `Detail.Data` on save). */
  get detailForm(): FormGroup {
    return this.formGroup.get('Detail') as FormGroup;
  }

  /** Dynamic attribute fields; maps to API `Detail.Data` as JSON on save. */
  get dataFieldsForm(): FormGroup {
    return this.detailForm.get('Data') as FormGroup;
  }

  onBannerSelectionChange(bannerId: string): void {
    this.formGroup.get('BannerId')?.setValue(bannerId ? bannerId : '');
    this.formGroup.get('BannerName')?.setValue(this.banners.find((b) => b.id === bannerId)?.name ?? '');
  }

  private resetFormForCreate(): void {
    this.formGroup.reset({
      Detail: {
        Id: '',
        ClientKey: '',
        UserId: '',
        PrintAttributeName: '',
        PrintAttributeType: '',
        Description: '',
        Status: 'active',
        LogicalVersions: '',
        LogicalPrintPage: '',
      },
      DivisionIds: [],
      DivisionNames: '',
      EventTypeName: '',
      BannerName: '',
      AssetNames: '',
      AssetId: '',
      EventTypeId: '',
      BannerId: '',
    });
    Object.keys(this.dataFieldsForm.controls).forEach((k) =>
      this.dataFieldsForm.removeControl(k, { emitEvent: false }),
    );
    this.dynamicFields = [];
    this.selectedAttributeType = '';
    this.showImageUpload = false;
    this.previousDivisionSelection = [];
    this.selectedAssetPreview = null;
    this.previewImageLoadFailed = false;
    this.sectionTitle = 'New Attribute';
    this.clearPrintAttributeEditFieldLocks();
  }

  private applyPrintAttributeEditFieldLocks(): void {
    this.detailForm.get('PrintAttributeType')?.disable({ emitEvent: false });
    this.detailForm.get('ClientKey')?.disable({ emitEvent: false });
    if(this.readOnlyMode) {
      this.formGroup.disable();
      this.detailForm.disable();
    }
  }

  private clearPrintAttributeEditFieldLocks(): void {
    this.detailForm.get('PrintAttributeType')?.enable({ emitEvent: false });
    this.detailForm.get('ClientKey')?.enable({ emitEvent: false });
    if(this.readOnlyMode) {
      this.formGroup.disable();
      this.detailForm.disable();
    }
  }

  ngOnInit(): void {
    // Load dropdown data from services
    this.loadDropdownData();

    this.route.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.printAttributeId = params['printAttributeId'] || '0';
      if (this.printAttributeId === '0') {
        this.isEditMode = false;
        this.resetFormForCreate();
      } else {
        this.isEditMode = true;
        this.loadPrintAttributeData();
      }
    });

    this.readOnlyMode = !this.authorizationService.checkAccess('edit||offermang');
    this.canEdit();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load dropdown data from services
   * Maps service data to DivisionOption format { id, name }
   */
  private loadDropdownData(): void {
    // Load divisions
    this.divisions$ = this.divisionService.divisionsList$.pipe(
      map(divisions => divisions.map(d => ({ id: d.Id, name: d.DivisionName }))),
      startWith([]),
      takeUntil(this.destroy$)
    );
    this.divisions$.subscribe(divisions => {
      this.divisions = divisions;
    });

    // Load event types
    this.eventTypes$ = this.eventTypeService.eventTypesList$.pipe(
      map(types => types.map(t => ({ id: t.Id, name: t.EventTypeName }))),
      startWith([]),
      takeUntil(this.destroy$)
    );
    this.eventTypes$.subscribe(types => {
      this.eventTypes = types;
    });

    // Load banners
    this.banners$ = this.bannersService.banners$.pipe(
      map((banners) =>
        banners.map((b) => ({ id: b.Id, name: b.BannerName })),
      ),
      startWith([]),
      takeUntil(this.destroy$)
    );
    this.banners$.subscribe(banners => {
      this.banners = banners;
    });
  }

  /**
   * Get dynamic field form control by field ID
   */
  getDynamicFieldControl(fieldId: string): FormControl {
    return this.dataFieldsForm.get(fieldId) as FormControl;
  }

  /**
   * When defs start with assetId + urlValue, render that pair on one row; remaining fields follow.
   */
  get assetPairFields(): DynamicField[] | null {
    const f = this.dynamicFields;
    if (f?.length >= 2 && f[0].id === 'assetId' && f[1].id === 'urlValue') {
      return [f[0], f[1]];
    }
    return null;
  }

  get remainingDynamicFields(): DynamicField[] {
    const f = this.dynamicFields;
    if (f?.length >= 2 && f[0].id === 'assetId' && f[1].id === 'urlValue') {
      return f.slice(2);
    }
    return f ?? [];
  }

  /** Two text/date fields (e.g. Header + Subheader) share one grid row under Asset ID + URL. */
  isRemainingTwoColumn(): boolean {
    return this.remainingDynamicFields.length === 2;
  }

  /** Remaining fields use grid (100% of cell) when exactly two; otherwise flex row basis from count. */
  flexForRemainingField(): string {
    if (this.isRemainingTwoColumn()) {
      return '100';
    }
    return this.getDynamicFieldFxFlex(this.remainingDynamicFields.length);
  }

  /**
   * `fxFlex` for Attribute Data fields — 1 / 2 / 3 columns (see ce-print-attr-detail mockup grid).
   */
  getDynamicFieldFxFlex(fieldCount?: number): string {
    const n = fieldCount ?? this.dynamicFields?.length ?? 0;
    if (n <= 1) {
      return '100';
    }
    if (n === 2 || n === 4) {
      return '49';
    }
    return '32';
  }

  /**
   * Render dynamic fields based on attribute type
   */
  private renderDynamicFields(attributeType: string): void {
    Object.keys(this.dataFieldsForm.controls).forEach((key) => {
      this.dataFieldsForm.removeControl(key);
    });
    this.dataFieldsForm.clearValidators();
    this.dateDataFieldIds = [];

    if (attributeType && this.dynamicFieldDefs[attributeType]) {
      this.dynamicFields = JSON.parse(JSON.stringify(this.dynamicFieldDefs[attributeType]));

      this.dynamicFields.forEach((field) => {
        const validators: ValidatorFn[] = [Validators.required];
        if (field.type === 'url') {
          validators.push(Validators.pattern(/^https?:\/\/.+/));
        }
        if (field.type === 'date') {
          this.dateDataFieldIds.push(field.id);
          const initial = field.value ? this.coerceDateForForm(field.value) : null;
          this.dataFieldsForm.addControl(
            field.id,
            new FormControl<Date | null>(initial, validators)
          );
        } else {
          this.dataFieldsForm.addControl(
            field.id,
            new FormControl(field.value || '', validators)
          );
        }
      });

      if (attributeType === 'sale_dates') {
        this.dataFieldsForm.setValidators(this.saleDatesRangeValidator);
      }
      this.dataFieldsForm.updateValueAndValidity({ emitEvent: false });
    } else {
      this.dynamicFields = [];
      this.dataFieldsForm.updateValueAndValidity({ emitEvent: false });
    }
  }

  formatDate(date: Date | string | null | undefined): string {
    if (date) {
      return dayjs(date).format('MM/DD/YYYY');
    }
    return '';
  }

  private coerceDateForForm(value: unknown): Date | null {
    if (!value) {
      return null;
    }
    const parsedDate = value instanceof Date ? value : new Date(String(value));
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  /** Maps dynamic `Detail.Data` form values to API `Data` (dates as MM/DD/YYYY). */
  private buildDataForPayload(): Record<string, string> {
    const raw = (this.dataFieldsForm.getRawValue() ?? {}) as Record<string, unknown>;
    const normalized: Record<string, string> = {};

    this.dynamicFields.forEach((field) => {
      const rawValue = raw[field.id];
      if (field.type === 'date') {
        normalized[field.id] = this.formatDate(rawValue as Date | string | null | undefined);
        return;
      }
      if (rawValue == null) {
        normalized[field.id] = '';
        return;
      }
      normalized[field.id] = String(rawValue);
    });

    return normalized;
  }

  /**
   * Get division names from division IDs (comma-separated)
   */
  private getDivisionNames(divisionIds: string[]): string {
    if (!divisionIds || divisionIds.length === 0) return '';
    
    // If only __all__ is in the array, return "All Divisions"
    if (divisionIds.length === 1 && divisionIds[0] === '__all__') {
      return 'All Divisions';
    }
    
    // Filter out __all__ placeholder and get normal division names
    const realIds = divisionIds.filter(id => id !== '__all__');
    const names = realIds
      .map(id => {
        const division = this.divisions.find(d => d.id === id);
        return division ? division.name : '';
      })
      .filter(name => name);
    return names.join(', ');
  }

  /** Coerce API / legacy values to a single string for the Versions text field. */
  private normalizeVersionsForForm(value: unknown): string {
    if (value == null || value === '') {
      return '';
    }
    if (Array.isArray(value)) {
      return value.map((v) => String(v).trim()).filter(Boolean).join(', ');
    }
    return String(value).trim();
  }

  /** Convert Versions input (string or array) into `string[]` for the API payload. */
  private parseVersionsForPayload(value: unknown): string[] {
    if (value == null || value === '') {
      return [];
    }
    if (Array.isArray(value)) {
      return value.map((v) => String(v).trim()).filter(Boolean);
    }
    return String(value)
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
  }

  private isAssetApplicableType(attributeType: string | null | undefined): boolean {
    if (!attributeType) {
      return false;
    }
    return this.imageRequiredTypes.includes(attributeType);
  }

  /** Read-only `Detail.Data` field filled from the asset picker (e.g. `CreativeAssetId`). */
  private getAssetDataFieldId(): string | null {
    const field = this.dynamicFields.find(
      (f) => f.readOnly && /AssetId$/i.test(f.id),
    );
    return field?.id ?? null;
  }

  /** True when attribute type shows asset upload / asset data fields. */
  get isAssetUploadRequired(): boolean {
    return this.showImageUpload || !!this.getAssetDataFieldId();
  }

  private updateAssetRequirementValidators(): void {
    const assetIdCtrl = this.formGroup.get('AssetId');
    if (!assetIdCtrl) {
      return;
    }
    if (this.isAssetUploadRequired) {
      assetIdCtrl.setValidators([Validators.required]);
    } else {
      assetIdCtrl.clearValidators();
      assetIdCtrl.setValue('', { emitEvent: false });
    }
    assetIdCtrl.updateValueAndValidity({ emitEvent: false });
  }

  /** Chip UI reads the same `Detail.Versions` string as comma-separated tokens (order preserved). */
  get versionsChipList(): string[] {
    return this.parseVersionsForPayload(this.detailForm.get('LogicalVersions')?.value);
  }

  readonly versionChipSeparatorKeysCodes: readonly number[] = [ENTER, COMMA];

  onVersionChipRemoved(index: number): void {
    const control = this.detailForm.get('LogicalVersions');
    if (!control) {
      return;
    }
    const next = [...this.parseVersionsForPayload(control.value)];
    if (index >= 0 && index < next.length) {
      next.splice(index, 1);
    }
    control.setValue(this.normalizeVersionsForForm(next));
  }

  onVersionChipInputEnd(event: MatChipInputEvent): void {
    const raw = (event.value ?? '').trim();
    if (!raw) {
      event.chipInput?.clear();
      return;
    }
    const control = this.detailForm.get('LogicalVersions');
    if (!control) {
      return;
    }
    const parts = raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    const current = [...this.parseVersionsForPayload(control.value)];
    for (const p of parts) {
      current.push(p);
    }
    control.setValue(this.normalizeVersionsForForm(current));
    event.chipInput?.clear();
  }

  /**
   * Handle "All Divisions" selection logic with mutual exclusivity
   * Mimics mockup behavior: when one option is selected, the other is unselected
   */
  onDivisionChange(event: any): void {
    const selectedValues = event.value || [];
    const control = this.formGroup.get('DivisionIds');
    if (!control) return;

    const allDivisionsControl = this.detailForm.get('AllDivisions');
    
    // Determine which option was just clicked by comparing with previous state
    const allWasPreviouslySelected = this.previousDivisionSelection.includes('__all__');
    const allIsNowSelected = selectedValues.includes('__all__');
    const individualWasAdded = selectedValues.some((v: string) => 
      v !== '__all__' && !this.previousDivisionSelection.includes(v)
    );

    // Case 1: "All Divisions" was just selected
    if (!allWasPreviouslySelected && allIsNowSelected) {
      // Uncheck all individual divisions
      control.setValue(['__all__'], { emitEvent: false });
      if (allDivisionsControl) {
        allDivisionsControl.setValue(true, { emitEvent: false });
      }
      this.previousDivisionSelection = ['__all__'];
    }
    // Case 2: An individual division was just selected while "All Divisions" was checked
    else if (allWasPreviouslySelected && individualWasAdded) {
      // Uncheck "All Divisions", keep only the individual selections
      const cleanedValues = selectedValues.filter((v: any) => v !== '__all__');
      control.setValue(cleanedValues, { emitEvent: false });
      if (allDivisionsControl) {
        allDivisionsControl.setValue(false, { emitEvent: false });
      }
      this.previousDivisionSelection = cleanedValues;
    }
    // Case 3: Normal change (no All Divisions involved or toggling it off)
    else {
      const cleanedValues = selectedValues.filter((v: any) => v !== '__all__');
      control.setValue(cleanedValues, { emitEvent: false });
      if (allDivisionsControl) {
        allDivisionsControl.setValue(false, { emitEvent: false });
      }
      this.previousDivisionSelection = cleanedValues;
    }

  }

  /**
   * Load existing print attribute data for editing
   */
  private loadPrintAttributeData(): void {
    this.printAttributeService
      .getPrintAttributeDefinition(this.printAttributeId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (attribute: PrintAttributeDefinition) => {
          const detailObj = attribute.Detail || {};

          this.formGroup.patchValue(
            {
              Detail: {
                Id: detailObj.Id ?? '',
                ClientKey: detailObj.ClientKey ?? '',
                UserId: detailObj.UserId ?? '',
                PrintAttributeName: detailObj.PrintAttributeName || '',
                PrintAttributeType:
                  detailObj.PrintAttributeType || attribute.PrintAttributeType || '',
                Description: detailObj.Description || '',
                Status: attribute.Detail?.Status || 'active',
                LogicalVersions: this.normalizeVersionsForForm(
                  attribute.Detail?.LogicalVersions,
                ),
                LogicalPrintPage: detailObj.LogicalPrintPage ?? '',
              },
              DivisionIds: attribute.DivisionIds || [],
              DivisionNames: attribute.DivisionNames || '',
              EventTypeName: attribute.EventTypeName || '',
              BannerName: attribute.BannerName || '',
              AssetId: attribute.AssetId || '',
              EventTypeId: attribute.EventTypeId ?? '',
              BannerId: attribute.BannerId ?? '',
            },
            { emitEvent: false },
          );

          const attrType = detailObj.PrintAttributeType;
          if (attrType) {
            this.selectedAttributeType = attrType;
            this.showImageUpload = this.isAssetApplicableType(attrType);
            this.renderDynamicFields(attrType);
            let valueFromDetail: Record<string, unknown> = {};
            const dataStr =
              typeof (detailObj as any).Data === 'string'
                ? String((detailObj as any).Data).trim()
                : '';
            const legacyValueStr =
              typeof (detailObj as any).Value === 'string'
                ? String((detailObj as any).Value).trim()
                : '';
            const jsonStr = dataStr || legacyValueStr;
            if (jsonStr) {
              try {
                const parsed = JSON.parse(jsonStr);
                if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                  valueFromDetail = parsed as Record<string, unknown>;
                }
              } catch {
                // ignore invalid JSON; fall back to CustomFields only
              }
              }

              const cf: Record<string, unknown> = {
                ...(valueFromDetail || {}),
                ...valueFromDetail,
              };

            Object.keys(cf).forEach((key) => {
              const field =
                this.dynamicFields.find((f) => f.id === key) ||
                this.dynamicFields.find((f) => f.id.toLowerCase() === key.toLowerCase());
              if (!field) {
                return;
              }
              const c = this.dataFieldsForm.get(field.id);
              if (!c) {
                return;
              }
              const val = cf[key];
              if (this.dateDataFieldIds.includes(field.id) && val) {
                c.setValue(this.coerceDateForForm(val), { emitEvent: false });
              } else {
                c.setValue(val, { emitEvent: false });
              }
            });

            // In edit mode, hydrate preview from already-saved asset fields.
            if (this.showImageUpload) {
              this.selectedAssetPreview = attribute.AssetData as unknown as AssetData;
            } else {
              this.selectedAssetPreview = null;
              this.previewImageLoadFailed = false;
            }
            this.updateAssetRequirementValidators();
          }

          this.sectionTitle = attrType ? `Edit ${attrType}` : 'Edit Attribute';
          this.applyPrintAttributeEditFieldLocks();
        },
        error: () => {
          this.matSnackbar.open(
            'Error loading print attribute',
            'OK',
            DEFAULT_SNACKBAR_CONFIG,
          );
        },
      });
  }

  /**
   * Save or update the print attribute
   */
  savePrintAttribute(): void {
    if (!this.formGroup.valid || this.readOnlyMode) {
      this.formGroup.markAllAsTouched();
      this.matSnackbar.open(
        'Please fix all errors before saving',
        'OK',
        DEFAULT_SNACKBAR_CONFIG,
      );
      return;
    }

    const raw = this.formGroup.getRawValue();
    if(!raw.Detail.ClientKey) {
      raw.Detail.ClientKey = uuidv4();
    }
    let divisionIds = [...(raw.DivisionIds || [])];

    const payload: any = {
      Detail: {...raw.Detail, LogicalVersions: this.parseVersionsForPayload(raw.Detail.LogicalVersions), LogicalPrintPage: raw.Detail.LogicalPrintPage ?? ''},
      DivisionIds: divisionIds,
      DivisionNames: this.getDivisionNames(divisionIds),
      BannerName: this.banners.find((b) => b.id === raw.BannerId)?.name ?? '',
      EventTypeId: raw.EventTypeId ?? '',
      BannerId: raw.BannerId ?? '',
      Data: this.buildDataForPayload(),
    };
    delete payload.Detail.Data;

    const assetId = String(raw.AssetId || this.selectedAssetPreview?.AssetId || '').trim();
    if (this.isAssetUploadRequired && assetId) {
      payload.AssetId = assetId;
      if (this.selectedAssetPreview) {
        payload.AssetData = this.selectedAssetPreview;
      }
    }

    if (this.isEditMode) {
      this.printAttributeService
        .updatePrintAttribute(this.printAttributeId, payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.matSnackbar.open(
              'Print attribute updated successfully',
              'OK',
              DEFAULT_SNACKBAR_CONFIG,
            );
            this.printAttributeService.reload();
            this.router.navigate(['../'], { relativeTo: this.route });
          },
          error: (e) => {
            this.matSnackbar.open(
              Array.isArray(e.error) ? e.error.map((err: any) => err.ErrorMessage).join(', ') : e.error?.message ?? 'Error updating print attribute',
              'OK',
              DEFAULT_SNACKBAR_CONFIG,
            );
          },
        });
    } else {
      this.printAttributeService
        .createPrintAttribute(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.matSnackbar.open(
              'Print attribute created successfully',
              'OK',
              DEFAULT_SNACKBAR_CONFIG,
            );
            this.printAttributeService.reload();
            this.router.navigate(['../'], { relativeTo: this.route });
          },
          error: (e) => {
            const msg = Array.isArray(e.error) ? e.error.map((err: any) => err.ErrorMessage).join(', ') : e.error?.message ?? 'Error creating print attribute';
            this.matSnackbar.open(msg, 'OK', DEFAULT_SNACKBAR_CONFIG);
          },
        });
    }
  }

  /**
   * Delete the print attribute
   */
  deletePrintAttribute(): void {
    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Confirm Delete',
        message: 'Are you sure you want to delete this print attribute?',
      },
      disableClose: true,
    });

    confirmDialog.afterClosed().subscribe((result) => {
      if (result === true) {
        this.printAttributeService
          .deletePrintAttributes([this.printAttributeId])
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.matSnackbar.open(
                'Print attribute deleted successfully',
                'OK',
                DEFAULT_SNACKBAR_CONFIG,
              );
              this.printAttributeService.reload();
              this.router.navigate(['../'], { relativeTo: this.route });
            },
            error: () => {
              this.matSnackbar.open(
                'Error deleting print attribute',
                'OK',
                DEFAULT_SNACKBAR_CONFIG,
              );
            },
          });
      }
    });
  }

  /**
   * Cancel and navigate back
   */
  cancel(): void {
    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Confirm Cancel',
        message: 'Are you sure you want to discard changes?',
      },
      disableClose: true,
    });

    confirmDialog.afterClosed().subscribe((result) => {
      if (result === true) {
        this.router.navigate(['../'], { relativeTo: this.route });
      }
    });
  }

  
  addAssetsToForm(): void {
    const dialogRef = this.dialog.open(AssetsSearchDialogComponent, {
      width: '100%',
      height: '100%',
      data: {
        isSingleSelect: true
      }
    });

    dialogRef.componentInstance.emitSlelectedAsset
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((assets: SearchAsset[]) => {
        if (!assets?.length) {
          return;
        }
        const first = assets[0];
        const webUrl = (first.webUrl || '').trim();
        this.formGroup.get('AssetId')?.setValue(first.assetId);
        this.formGroup.get('AssetNames')?.setValue(
          assets.map((a) => a.assetName || a.fileName || a.assetId).join(', '),
        );
        this.detailForm.get('MasterDefaultAssetIds')?.setValue(
          assets.map((a) => a.assetId),
        );
        const assetIdValue = assets.map((a) => a.assetId).join(', ');
        const assetDataFieldId = this.getAssetDataFieldId();
        if (assetDataFieldId) {
          this.dataFieldsForm.get(assetDataFieldId)?.setValue(assetIdValue);
        }

        this.previewImageLoadFailed = false;
        const typeLabel = first.fileType || first.assetType || '';
        this.selectedAssetPreview = {
          AssetId: first.assetId,
          AssetName: first.assetName,
          FileName: first.fileName,
          FileUrl: webUrl,
          FileType: typeLabel,
          InValidFileUrl: null,
          Captions: [{
            FileUrl: webUrl,
            LanguageDisplayName: "ENG",
            Language: "ENG",
          }]
        };
      });
  }

  /** Stable accent for placeholder preview when image URL is missing or fails to load. */
  private previewAccentFromAssetId(assetId: string): string {
    const palette = [
      '#4CAF50',
      '#2196F3',
      '#FF9800',
      '#E91E63',
      '#9C27B0',
      '#009688',
      '#607D8B',
      '#FF5722',
      '#3F51B5',
      '#795548',
    ];
    let h = 0;
    for (let i = 0; i < assetId.length; i++) {
      h = assetId.charCodeAt(i) + ((h << 5) - h);
    }
    return palette[Math.abs(h) % palette.length];
  }

  onAssetPreviewImageError(): void {
    this.previewImageLoadFailed = true;
  }

  removeSelectedAsset(): void {
    this.selectedAssetPreview = null;
    this.previewImageLoadFailed = false;
    this.formGroup.get('AssetId')?.setValue('');
    this.formGroup.get('AssetNames')?.setValue('');
    const assetDataFieldId = this.getAssetDataFieldId();
    if (assetDataFieldId) {
      this.dataFieldsForm.get(assetDataFieldId)?.setValue('');
    }
  }

  canEdit() {
    if(this.readOnlyMode) {
      this.formGroup.disable();
      this.detailForm.disable();
    }
    return !this.readOnlyMode;
  }
}
